import { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
} from 'recharts';
import { buildDivergenceSeries, buildDivergenceSeriesFromReal, CHART_DISCLAIMER } from '../../utils/chartApprox';
import { T, fmtMoney, resolveChartColors } from '../theme';
import { AsteriskNote, LegendSwatch, ttStyle } from '../primitives';
import type { OptimizationVM } from '../../formatters/deckContent';
import type { WebReportPayload } from '../../types/webReport';

interface Props {
  vm: OptimizationVM;
  payload: WebReportPayload;
  optimized: boolean;
  /** When set: fixed-size chart (print) with animations disabled. */
  width?: number;
}

const BUCKET_COLOR_KEYS = ['teal', 'blue', 'navy', 'gray'] as const;

/** Framing 2 — divergence line (Align to Time Horizons). */
export function DivergencePanel({ vm, payload, optimized, width }: Props) {
  const colors = useMemo(() => resolveChartColors(), []);
  // Real per-year series from SIPS (schema v8) when present; synthesized
  // endpoint-pinned curve for older reports.
  const isReal = !!(vm.alignmentSeries && vm.alignmentSeries.length > 1);
  const series = useMemo(
    () =>
      vm.alignmentSeries && vm.alignmentSeries.length > 1
        ? buildDivergenceSeriesFromReal(vm.alignmentSeries)
        : buildDivergenceSeries({
            totalSavings: payload.client.totalSavings,
            planHorizonYears: payload.client.planHorizonYears,
            wealthAdded: vm.valueAdded,
          }),
    [payload, vm.valueAdded, vm.alignmentSeries]
  );

  const animated = width == null;
  const buckets = vm.buckets || [];
  const bucketColor = (key: (typeof BUCKET_COLOR_KEYS)[number]) => colors[key];

  const chart = (
    <LineChart
      data={series}
      margin={{ top: 20, right: 12, left: 14, bottom: 22 }}
      {...(width != null ? { width, height: 300 } : {})}
    >
      <CartesianGrid vertical={false} stroke={colors.grayLine} />
      <XAxis dataKey="year" tick={{ fontSize: 11, fill: colors.gray }} tickLine={false} axisLine={{ stroke: colors.grayLine }}>
        <Label value="Years into plan" position="bottom" offset={6} style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600 }} />
      </XAxis>
      <YAxis
        domain={[0, 'auto']}
        tick={{ fontSize: 11, fill: colors.gray }}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v) => `$${v}M`}
      >
        <Label
          value="Total savings ($M)"
          angle={-90}
          position="insideLeft"
          offset={10}
          style={{ fontSize: 11.5, fill: colors.gray, fontWeight: 600, textAnchor: 'middle' }}
        />
      </YAxis>
      <Tooltip contentStyle={ttStyle} formatter={(v) => `$${v}M`} />
      <Line
        type="monotone"
        dataKey="starting"
        name="Starting"
        stroke={colors.gray}
        strokeWidth={2.5}
        dot={false}
        isAnimationActive={animated}
      />
      {optimized && (
        <Line
          type="monotone"
          dataKey="optimized"
          name="Optimized"
          stroke={colors.growth}
          strokeWidth={3}
          dot={false}
          isAnimationActive={animated}
          animationDuration={700}
        />
      )}
    </LineChart>
  );

  return (
    <div className="deck-chart-block">
      <div style={{ fontWeight: 600, color: T.navy, fontSize: 16, marginBottom: 2 }}>
        {optimized
          ? 'Bucketed allocation pulls ahead'
          : isReal
            ? 'Your starting trajectory'
            : 'Your starting trajectory at a flat 5%'}
      </div>
      <div style={{ fontSize: 13, color: T.gray, marginBottom: 16 }}>
        {optimized ? `Same horizon, +${fmtMoney(vm.valueAdded)} by the end.` : 'Are we leaving money on the table?'}
      </div>
      {width != null ? chart : <ResponsiveContainer width="100%" height={300}>{chart}</ResponsiveContainer>}
      {optimized && (
        <div style={{ display: 'inline-block', background: T.growthSoft, borderRadius: 8, padding: '8px 14px', marginTop: 6 }}>
          <span style={{ fontWeight: 700, color: T.growth, fontSize: 15 }}>+{fmtMoney(vm.valueAdded)}</span>
          <span style={{ color: T.growth, fontSize: 12.5, marginLeft: 6 }}>added value from time-horizon alignment</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 18, marginTop: 14, fontSize: 12, color: T.gray }}>
        <LegendSwatch color={colors.gray} label={isReal ? 'Starting (before alignment)' : 'Starting (5% flat)'} />
        {optimized && <LegendSwatch color={colors.growth} label="Optimized (bucketed)" />}
      </div>

      {/* bucket cards */}
      {buckets.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(4, buckets.length)},1fr)`, gap: 10, marginTop: 18 }}>
          {buckets.map((b) => (
            <div key={b.name} style={{ borderTop: `3px solid ${bucketColor(b.colorKey)}`, paddingTop: 10 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: T.navy }}>{b.name}</div>
              <div style={{ fontSize: 10.5, color: T.gray, marginBottom: 6 }}>{b.years}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: T.navy }}>{fmtMoney(b.amount)}</div>
              <div style={{ fontSize: 10.5, color: T.gray }}>{b.note}</div>
            </div>
          ))}
        </div>
      )}

      <AsteriskNote text={CHART_DISCLAIMER} />
    </div>
  );
}
